import { useState } from 'react';

interface AuthGateNoticeProps {
  /** What to tell the user. */
  message: string;
  /**
   * True when the problem is reachability (slow/dropped connection), not the
   * account. Changes both the framing and, crucially, what the buttons DO.
   */
  retryable: boolean;
  /** Re-fetch the profile without touching the session. Retryable case only. */
  onRetry: () => Promise<void> | void;
  /** Sign out and send the user back to the login screen. */
  onSignOut: () => Promise<void> | void;
}

/**
 * The full-screen notice shown when the app has a session but no usable
 * profile.
 *
 * Before this, there was one screen — "Authentication Issue … no profile was
 * found, please contact support" — with two buttons that BOTH signed the user
 * out. That is the right screen for an account whose profile row is genuinely
 * missing, and the wrong one for the far more common case of a slow backend:
 * a franchise manager on a bad connection got told their account was broken
 * and was thrown back to the login page, repeatedly (ticket 960d02e5, "slow to
 * load and sometimes errors out … once I sign back in it works for a little
 * bit, then errors out again").
 *
 * So the retryable case retries: `onRetry` re-fetches the profile on the
 * session that is still perfectly valid. Signing out is demoted to the
 * secondary action, because signing out is what made this a recurring
 * annoyance rather than a blip.
 */
export function AuthGateNotice({ message, retryable, onRetry, onSignOut }: AuthGateNoticeProps) {
  const [busy, setBusy] = useState(false);

  const run = async (action: () => Promise<void> | void) => {
    if (busy) return;
    setBusy(true);
    try {
      await action();
    } finally {
      // On the retryable path a successful retry unmounts this screen; if it
      // failed we stay put, and the button has to be clickable again.
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center max-w-md mx-auto p-6">
        <div className="text-6xl mb-4" aria-hidden="true">{retryable ? '📶' : '⚠️'}</div>
        <h1 className="text-2xl font-bold mb-4">
          {retryable ? "Couldn't load your account" : 'Authentication Issue'}
        </h1>
        <p className="text-muted-foreground mb-4">{message}</p>
        {/* Primary is ALWAYS the non-destructive re-fetch. Even on the
            "no profile" case that is the better first move: the session is
            valid, so there is nothing signing out can fix that a re-read
            can't, and signing out is what turned a blip into a loop. */}
        <button
          onClick={() => run(onRetry)}
          disabled={busy}
          className="bg-primary text-primary-foreground px-4 py-2 rounded mr-4 disabled:opacity-60"
        >
          {busy ? 'Trying…' : 'Try again'}
        </button>
        <button
          onClick={() => run(onSignOut)}
          disabled={busy}
          className="bg-secondary text-secondary-foreground px-4 py-2 rounded disabled:opacity-60"
        >
          {retryable ? 'Sign out' : 'Back to Login'}
        </button>
      </div>
    </div>
  );
}
